import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Trash2, TrendingDown, TrendingUp, History, Upload, ShoppingCart, Camera, Plus } from "lucide-react";
import PageHeader from "@/app/PageHeader";
import { useSession, errText } from "@/app/session";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const S = "/admin/sourcing";
const money = (v, digits = 2) => (v == null ? "—" : `$${Number(v).toFixed(digits)}`);
const field = "border-white/10 bg-vortex-ink/60 text-slate-100 focus-visible:ring-vortex-cyan";
const select = `h-9 w-full rounded-md border px-3 text-sm ${field}`;
const lbl = "font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase";
const card = "border border-white/10 bg-vortex-surface/60 p-6";
const btn = "flex items-center justify-center gap-2 rounded-full bg-vortex-cyan px-6 py-3 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink disabled:opacity-50";
const ghost = "flex items-center gap-2 rounded-full border border-white/15 px-5 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-slate-300 hover:border-vortex-cyan/60 hover:text-vortex-cyan";
const th = "px-3 py-2 text-left font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase";
const td = "px-3 py-3 align-top text-sm text-slate-200";

const Field = ({ label, id, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className={lbl}>{label}</Label>
    {children}
  </div>
);

const today = () => new Date().toISOString().slice(0, 10);
const emptyPrice = { product_id: "", store_id: "", price: "", pack_qty: "1", on_sale: false, regular_price: null, seen_on: today(), note: "" };
const emptyProduct = { name: "", brand: "", size: "", category: "", upc: "", case_pack: "1", vend_price: "", target_unit_cost: "" };
const emptyStore = { name: "", chain: "", address: "", membership: false };

const numOrNull = (v) => (v === "" || v == null ? null : Number(v));

// Shrink a camera photo before upload; tag text stays readable at this size.
const shrinkPhoto = (file, maxSide = 1568) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    resolve(canvas.toDataURL("image/jpeg", 0.85));
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not open that photo.")); };
  img.src = url;
});

const Sourcing = () => {
  const { token, api } = useSession();
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [priceForm, setPriceForm] = useState(emptyPrice);
  const [productForm, setProductForm] = useState(emptyProduct);
  const [storeForm, setStoreForm] = useState(emptyStore);
  const [planQty, setPlanQty] = useState({});
  const [planStores, setPlanStores] = useState([]);
  const [plan, setPlan] = useState(null);
  const [csvText, setCsvText] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);
  const [scanReady, setScanReady] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [photo, setPhoto] = useState("");
  const [scanInfo, setScanInfo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, s] = await Promise.all([
        api.get(`${S}/compare`), api.get(`${S}/products`), api.get(`${S}/stores`)]);
      setRows(c.data); setProducts(p.data); setStores(s.data);
    } catch (err) {
      if (err.response?.status !== 401) toast.error(errText(err, "Could not load sourcing data."));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { if (token) load(); }, [token, load]);

  useEffect(() => {
    if (!token) return;
    api.get(`${S}/scan/status`).then((r) => setScanReady(!!r.data.configured)).catch(() => setScanReady(false));
  }, [token, api]);

  const scanPhoto = async (file) => {
    if (!file) return;
    setScanning(true);
    setScanInfo(null);
    try {
      const image = await shrinkPhoto(file);
      setPhoto(image);
      const { data } = await api.post(`${S}/scan`, { image });
      const r = data.reading || {};
      if (!r.is_price_tag || r.price == null) {
        toast.error("Couldn't find a price in that photo. Try again closer to the tag.");
        setScanInfo({ notes: r.notes });
        return;
      }
      setPriceForm((f) => ({
        ...f,
        product_id: data.product_id || f.product_id,
        store_id: f.store_id || data.store_id || "",
        price: String(r.price),
        pack_qty: String(r.pack_qty || 1),
        on_sale: !!r.on_sale,
        regular_price: r.regular_price ?? null,
      }));
      setScanInfo({ ...r, matched: !!data.product_id });
      toast.success("Price read. Check it, then save.");
    } catch (err) {
      if (err.response?.status !== 401) toast.error(errText(err, err.message || "Could not read that photo."));
    } finally {
      setScanning(false);
    }
  };

  const addScannedProduct = async () => {
    const res = await run(() => api.post(`${S}/products`, {
      name: scanInfo.product_name, brand: scanInfo.brand || "", size: scanInfo.size || "", category: "Other",
    }), (r) => `"${r.data.name}" added.`);
    if (res) {
      setPriceForm((f) => ({ ...f, product_id: res.data.id }));
      setScanInfo((i) => ({ ...i, matched: true }));
    }
  };

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const res = await fn();
      if (ok) toast.success(typeof ok === "function" ? ok(res) : ok);
      await load();
      return res;
    } catch (err) {
      if (err.response?.status !== 401) toast.error(errText(err));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.product.name} ${r.product.brand} ${r.product.category}`.toLowerCase().includes(q));
  }, [rows, filter]);

  const unitPreview = priceForm.price && priceForm.pack_qty
    ? Number(priceForm.price) / Math.max(Number(priceForm.pack_qty), 1) : null;

  const logPrice = async (e) => {
    e.preventDefault();
    const res = await run(() => api.post(`${S}/prices`, {
      ...priceForm, price: Number(priceForm.price), pack_qty: Number(priceForm.pack_qty) || 1,
      seen_on: priceForm.seen_on || null,
    }), "Price saved.");
    // Keep the store and date so several items from one trip go in quickly.
    if (res) {
      setPriceForm({ ...emptyPrice, store_id: priceForm.store_id, seen_on: priceForm.seen_on });
      setPhoto("");
      setScanInfo(null);
    }
  };

  const addProduct = async (e) => {
    e.preventDefault();
    const res = await run(() => api.post(`${S}/products`, {
      ...productForm, category: productForm.category || "Other", case_pack: Number(productForm.case_pack) || 1,
      vend_price: numOrNull(productForm.vend_price), target_unit_cost: numOrNull(productForm.target_unit_cost),
    }), (r) => `"${r.data.name}" added.`);
    if (res) setProductForm(emptyProduct);
  };

  const addStore = async (e) => {
    e.preventDefault();
    const res = await run(() => api.post(`${S}/stores`, storeForm), (r) => `"${r.data.name}" added.`);
    if (res) setStoreForm(emptyStore);
  };

  const removeProduct = (p) => {
    if (!window.confirm(`Delete "${p.name}" and its price history?`)) return;
    run(() => api.delete(`${S}/products/${p.id}`), "Product deleted.");
  };

  const removeStore = (s) => {
    if (!window.confirm(`Delete "${s.name}" and every price logged there?`)) return;
    run(() => api.delete(`${S}/stores/${s.id}`), "Store deleted.");
  };

  const buildPlan = async (e) => {
    e.preventDefault();
    const items = Object.entries(planQty)
      .filter(([, u]) => Number(u) > 0)
      .map(([product_id, units]) => ({ product_id, units: Number(units) }));
    if (!items.length) { toast.error("Enter units for at least one product."); return; }
    setBusy(true);
    try {
      const res = await api.post(`${S}/plan`, { items, store_ids: planStores.length ? planStores : null });
      setPlan(res.data);
    } catch (err) {
      if (err.response?.status !== 401) toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const fillPlanFromCasePacks = () => {
    const next = {};
    products.forEach((p) => { next[p.id] = p.case_pack || 1; });
    setPlanQty(next);
  };

  const importCsv = async (e) => {
    e.preventDefault();
    const res = await run(() => api.post(`${S}/import`, { csv: csvText }),
      (r) => `Added ${r.data.prices_added} prices and ${r.data.products_created} new products.`);
    if (res) {
      if (res.data.error_count) toast.warning(`${res.data.error_count} rows skipped: ${res.data.errors.slice(0, 3).join("; ")}`);
      else setCsvText("");
    }
  };

  const readFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(file);
  };

  const openHistory = async (p) => {
    try {
      const res = await api.get(`${S}/products/${p.id}/history`);
      setHistory({ product: p, rows: res.data });
    } catch (err) {
      if (err.response?.status !== 401) toast.error(errText(err));
    }
  };

  const deletePrice = async (id) => {
    await run(() => api.delete(`${S}/prices/${id}`), "Price removed.");
    if (history) openHistory(history.product);
  };

  return (
    <div data-testid="sourcing-page">
      <PageHeader title="Stock Sourcing" testId="sourcing-header"
        intro="Log shelf prices as you shop, see the cheapest store for every product, and build a shopping run split by store."
        actions={<button onClick={load} className={ghost}><RefreshCw size={14} /> Refresh</button>} />
        <Tabs defaultValue="prices">
          <TabsList className="flex h-auto flex-wrap gap-1 bg-vortex-surface/60">
            <TabsTrigger value="prices">Prices</TabsTrigger>
            <TabsTrigger value="log">Log a price</TabsTrigger>
            <TabsTrigger value="plan">Shopping run</TabsTrigger>
            <TabsTrigger value="catalog">Products & stores</TabsTrigger>
            <TabsTrigger value="import">Import</TabsTrigger>
          </TabsList>

          <TabsContent value="prices" className="mt-6">
            <Input placeholder="Filter products" value={filter} onChange={(e) => setFilter(e.target.value)} className={`max-w-sm ${field}`} />
            <div className="mt-4 overflow-x-auto border border-white/10">
              <table className="w-full min-w-[760px]">
                <thead className="bg-vortex-surface/60">
                  <tr>
                    <th className={th}>Product</th><th className={th}>Best store</th><th className={th}>Unit cost</th>
                    <th className={th}>Price to beat</th><th className={th}>Margin</th><th className={th}>Other stores</th><th className={th} />
                  </tr>
                </thead>
                <tbody>
                  {loading && !rows.length && <tr><td className={td} colSpan={7}>Loading…</td></tr>}
                  {!loading && !visible.length && (
                    <tr><td className={`${td} text-slate-400`} colSpan={7}>
                      No products yet. Add products and stores, then log a price.
                    </td></tr>
                  )}
                  {visible.map((r) => (
                    <tr key={r.product.id} className="border-t border-white/5">
                      <td className={td}>
                        <div className="font-medium text-white">{r.product.name}</div>
                        <div className="text-xs text-slate-500">{[r.product.brand, r.product.size].filter(Boolean).join(" · ")}</div>
                        {r.buy_now && <span className="mt-1 inline-block rounded-full bg-emerald-500/15 px-2 py-0.5 font-mono text-[10px] uppercase text-emerald-300">Buy now</span>}
                      </td>
                      <td className={td}>
                        {r.best ? (<>
                          <div>{r.best.store}{r.best.on_sale && <span className="ml-2 text-xs text-amber-300">sale</span>}</div>
                          <div className="text-xs text-slate-500">{money(r.best.price)} / {r.best.pack_qty} · {r.best.seen_on}</div>
                        </>) : <span className="text-slate-500">No recent price</span>}
                      </td>
                      <td className={td}>
                        {money(r.best?.unit_price, 3)}
                        {r.change_pct != null && r.change_pct !== 0 && (
                          <span className={`ml-2 inline-flex items-center gap-1 text-xs ${r.change_pct < 0 ? "text-emerald-300" : "text-red-300"}`}>
                            {r.change_pct < 0 ? <TrendingDown size={12} /> : <TrendingUp size={12} />}{Math.abs(r.change_pct)}%
                          </span>
                        )}
                      </td>
                      <td className={td}>{money(r.price_to_beat, 3)}</td>
                      <td className={td}>{r.margin_pct == null ? "—" : `${r.margin_pct}%`}</td>
                      <td className={`${td} text-xs text-slate-400`}>
                        {r.stores.filter((s) => s.store_id !== r.best?.store_id).slice(0, 3).map((s) => (
                          <div key={s.store_id} className={s.stale ? "opacity-50" : ""}>{s.store}: {money(s.unit_price, 3)}{s.stale ? " (old)" : ""}</div>
                        ))}
                      </td>
                      <td className={td}>
                        <button onClick={() => openHistory(r.product)} className="text-slate-400 hover:text-vortex-cyan" aria-label={`Price history for ${r.product.name}`}>
                          <History size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Best store uses each store's latest price from the last 60 days. Price to beat is the 90-day average, with sale prices counted at half weight.
            </p>
          </TabsContent>

          <TabsContent value="log" className="mt-6">
            <form onSubmit={logPrice} className={`${card} grid max-w-2xl gap-5 sm:grid-cols-2`}>
              <div className="sm:col-span-2 space-y-3">
                {scanReady ? (
                  <label className={`${btn} w-full cursor-pointer ${scanning ? "pointer-events-none opacity-50" : ""}`}>
                    <Camera size={16} /> {scanning ? "Reading the tag…" : "Take a photo of the price tag"}
                    <input type="file" accept="image/*" capture="environment" className="sr-only" disabled={scanning}
                      onChange={(e) => { scanPhoto(e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                ) : (
                  <p className="text-xs text-slate-500">Photo reading isn't switched on yet, so type the price below.</p>
                )}
                {photo && (
                  <div className="flex gap-4">
                    <img src={photo} alt="Price tag photo" className="h-28 w-28 rounded-md border border-white/10 object-cover" />
                    {scanInfo && (
                      <div className="space-y-1 text-sm text-slate-300">
                        {scanInfo.product_name && <p>Read: <span className="text-white">{scanInfo.product_name}</span>{scanInfo.price != null ? ` · ${money(scanInfo.price)}${scanInfo.pack_qty > 1 ? ` for ${scanInfo.pack_qty}` : ""}` : ""}</p>}
                        {scanInfo.product_name && !scanInfo.matched && (
                          <button type="button" onClick={addScannedProduct} disabled={busy} className={ghost}>
                            <Plus size={12} /> Add "{scanInfo.product_name}" as a product
                          </button>
                        )}
                        {scanInfo.notes && <p className="text-xs text-amber-300">{scanInfo.notes}</p>}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <Field label="Product" id="lp-product">
                <select id="lp-product" required className={select} value={priceForm.product_id}
                  onChange={(e) => setPriceForm({ ...priceForm, product_id: e.target.value })}>
                  <option value="">Choose…</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}{p.size ? ` (${p.size})` : ""}</option>)}
                </select>
              </Field>
              <Field label="Store" id="lp-store">
                <select id="lp-store" required className={select} value={priceForm.store_id}
                  onChange={(e) => setPriceForm({ ...priceForm, store_id: e.target.value })}>
                  <option value="">Choose…</option>
                  {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Shelf price" id="lp-price">
                <Input id="lp-price" required type="number" step="0.01" min="0.01" inputMode="decimal" className={field}
                  value={priceForm.price} onChange={(e) => setPriceForm({ ...priceForm, price: e.target.value })} placeholder="21.98" />
              </Field>
              <Field label="Units in pack" id="lp-pack">
                <Input id="lp-pack" required type="number" min="1" inputMode="numeric" className={field}
                  value={priceForm.pack_qty} onChange={(e) => setPriceForm({ ...priceForm, pack_qty: e.target.value })} />
              </Field>
              <Field label="Date seen" id="lp-date">
                <Input id="lp-date" type="date" className={field} value={priceForm.seen_on}
                  onChange={(e) => setPriceForm({ ...priceForm, seen_on: e.target.value })} />
              </Field>
              <label className="flex items-center gap-3 self-end pb-2 text-sm text-slate-300">
                <input type="checkbox" checked={priceForm.on_sale}
                  onChange={(e) => setPriceForm({ ...priceForm, on_sale: e.target.checked })} /> On sale
              </label>
              <div className="sm:col-span-2 flex items-center justify-between gap-4">
                <span className="text-sm text-slate-400">{unitPreview ? `${money(unitPreview, 3)} per unit` : ""}</span>
                <button type="submit" disabled={busy} className={btn}>Save price</button>
              </div>
              {(!products.length || !stores.length) && (
                <p className="sm:col-span-2 text-xs text-amber-300">Add at least one product and one store under Products & stores first.</p>
              )}
            </form>
          </TabsContent>

          <TabsContent value="plan" className="mt-6">
            <form onSubmit={buildPlan} className="grid gap-8 lg:grid-cols-12">
              <div className={`${card} lg:col-span-5`}>
                <div className="flex items-center justify-between">
                  <p className={lbl}>Units needed</p>
                  <button type="button" onClick={fillPlanFromCasePacks} className="font-mono text-[10px] uppercase text-slate-400 hover:text-vortex-cyan">One case each</button>
                </div>
                <div className="mt-4 max-h-[420px] space-y-2 overflow-y-auto pr-2">
                  {products.map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-3">
                      <span className="text-sm text-slate-200">{p.name}</span>
                      <Input type="number" min="0" inputMode="numeric" className={`w-24 ${field}`} value={planQty[p.id] ?? ""}
                        onChange={(e) => setPlanQty({ ...planQty, [p.id]: e.target.value })} aria-label={`Units of ${p.name}`} />
                    </div>
                  ))}
                  {!products.length && <p className="text-sm text-slate-500">No products yet.</p>}
                </div>
                <p className={`${lbl} mt-6`}>Only these stores (optional)</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {stores.map((s) => {
                    const on = planStores.includes(s.id);
                    return (
                      <button type="button" key={s.id}
                        onClick={() => setPlanStores(on ? planStores.filter((x) => x !== s.id) : [...planStores, s.id])}
                        className={`rounded-full border px-3 py-1 text-xs ${on ? "border-vortex-cyan text-vortex-cyan" : "border-white/15 text-slate-400"}`}>
                        {s.name}
                      </button>
                    );
                  })}
                </div>
                <button type="submit" disabled={busy} className={`${btn} mt-6 w-full`}><ShoppingCart size={14} /> Build shopping run</button>
              </div>
              <div className="space-y-6 lg:col-span-7">
                {!plan && <p className="text-sm text-slate-500">Enter the units you need and build a run. Each item goes to the store with its lowest recent unit cost.</p>}
                {plan && (<>
                  <p className="font-heading text-2xl font-black text-white">Total {money(plan.grand_total)}</p>
                  {plan.stops.map((stop) => (
                    <div key={stop.store_id} className={card}>
                      <div className="flex items-baseline justify-between">
                        <p className="font-medium text-white">{stop.store}</p>
                        <p className="text-sm text-vortex-cyan">{money(stop.total)}</p>
                      </div>
                      <ul className="mt-3 space-y-1 text-sm text-slate-300">
                        {stop.items.map((it) => (
                          <li key={it.product_id} className="flex justify-between gap-4">
                            <span>{it.packs} × {it.name} ({it.pack_qty}-pack @ {money(it.pack_price)}){it.on_sale ? " · sale" : ""}</span>
                            <span>{money(it.cost)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  {plan.missing.length > 0 && (
                    <p className="text-sm text-amber-300">No recent price for: {plan.missing.map((m) => m.name).join(", ")}</p>
                  )}
                </>)}
              </div>
            </form>
          </TabsContent>

          <TabsContent value="catalog" className="mt-6">
            <div className="grid gap-8 lg:grid-cols-2">
              <div className="space-y-4">
                <form onSubmit={addProduct} className={`${card} grid gap-4 sm:grid-cols-2`}>
                  <p className={`${lbl} sm:col-span-2`}>Add product</p>
                  <Field label="Name" id="np-name"><Input id="np-name" required className={field} value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} placeholder="Coca-Cola 20oz" /></Field>
                  <Field label="Brand" id="np-brand"><Input id="np-brand" className={field} value={productForm.brand} onChange={(e) => setProductForm({ ...productForm, brand: e.target.value })} /></Field>
                  <Field label="Size" id="np-size"><Input id="np-size" className={field} value={productForm.size} onChange={(e) => setProductForm({ ...productForm, size: e.target.value })} placeholder="20 oz" /></Field>
                  <Field label="Category" id="np-cat"><Input id="np-cat" className={field} value={productForm.category} onChange={(e) => setProductForm({ ...productForm, category: e.target.value })} placeholder="Soda" /></Field>
                  <Field label="Vend price" id="np-vend"><Input id="np-vend" type="number" step="0.01" min="0" className={field} value={productForm.vend_price} onChange={(e) => setProductForm({ ...productForm, vend_price: e.target.value })} placeholder="2.75" /></Field>
                  <Field label="Target unit cost" id="np-target"><Input id="np-target" type="number" step="0.01" min="0" className={field} value={productForm.target_unit_cost} onChange={(e) => setProductForm({ ...productForm, target_unit_cost: e.target.value })} placeholder="0.80" /></Field>
                  <Field label="Units per case" id="np-case"><Input id="np-case" type="number" min="1" className={field} value={productForm.case_pack} onChange={(e) => setProductForm({ ...productForm, case_pack: e.target.value })} /></Field>
                  <Field label="UPC" id="np-upc"><Input id="np-upc" className={field} value={productForm.upc} onChange={(e) => setProductForm({ ...productForm, upc: e.target.value })} /></Field>
                  <button type="submit" disabled={busy} className={`${btn} sm:col-span-2`}>Add product</button>
                </form>
                <ul className="divide-y divide-white/5 border border-white/10">
                  {products.map((p) => (
                    <li key={p.id} className="flex items-center justify-between px-4 py-3 text-sm text-slate-200">
                      <span>{p.name} <span className="text-slate-500">{[p.size, p.vend_price != null && `vends ${money(p.vend_price)}`].filter(Boolean).join(" · ")}</span></span>
                      <button onClick={() => removeProduct(p)} aria-label={`Delete ${p.name}`} className="text-slate-500 hover:text-red-400"><Trash2 size={14} /></button>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-4">
                <form onSubmit={addStore} className={`${card} grid gap-4`}>
                  <p className={lbl}>Add store</p>
                  <Field label="Name" id="ns-name"><Input id="ns-name" required className={field} value={storeForm.name} onChange={(e) => setStoreForm({ ...storeForm, name: e.target.value })} placeholder="Costco Exton" /></Field>
                  <Field label="Address" id="ns-addr"><Input id="ns-addr" className={field} value={storeForm.address} onChange={(e) => setStoreForm({ ...storeForm, address: e.target.value })} /></Field>
                  <label className="flex items-center gap-3 text-sm text-slate-300">
                    <input type="checkbox" checked={storeForm.membership} onChange={(e) => setStoreForm({ ...storeForm, membership: e.target.checked })} /> Membership required
                  </label>
                  <button type="submit" disabled={busy} className={btn}>Add store</button>
                </form>
                <ul className="divide-y divide-white/5 border border-white/10">
                  {stores.map((s) => (
                    <li key={s.id} className="flex items-center justify-between px-4 py-3 text-sm text-slate-200">
                      <span>{s.name} <span className="text-slate-500">{[s.address, s.membership && "membership"].filter(Boolean).join(" · ")}</span></span>
                      <button onClick={() => removeStore(s)} aria-label={`Delete ${s.name}`} className="text-slate-500 hover:text-red-400"><Trash2 size={14} /></button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="import" className="mt-6">
            <form onSubmit={importCsv} className={`${card} max-w-3xl space-y-4`}>
              <p className="text-sm text-slate-300">
                Paste or upload a CSV from a receipt or order history. Columns: <code>product, store, price</code>, plus
                optional <code>pack_qty, on_sale, regular_price, date (YYYY-MM-DD), upc, brand, size, category, vend_price</code>.
                Missing products and stores are created. Rows without a price just add the product.
              </p>
              <input type="file" accept=".csv,text/csv" onChange={(e) => readFile(e.target.files?.[0])} className="text-sm text-slate-400" />
              <textarea rows={10} value={csvText} onChange={(e) => setCsvText(e.target.value)}
                className={`w-full rounded-md border p-3 font-mono text-xs ${field}`}
                placeholder={"product,store,price,pack_qty,on_sale,date\nCoca-Cola 20oz,Sam's Club,21.98,24,,2026-10-01"} />
              <button type="submit" disabled={busy || !csvText.trim()} className={btn}><Upload size={14} /> Import</button>
            </form>
          </TabsContent>
        </Tabs>

      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="max-w-2xl border-white/10 bg-vortex-surface text-slate-100">
          <DialogHeader>
            <DialogTitle>{history?.product.name}</DialogTitle>
            <DialogDescription>Every price logged for this product, newest first.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full">
              <thead><tr><th className={th}>Date</th><th className={th}>Store</th><th className={th}>Price</th><th className={th}>Unit</th><th className={th}>Source</th><th className={th} /></tr></thead>
              <tbody>
                {history?.rows.map((h) => (
                  <tr key={h.id} className="border-t border-white/5">
                    <td className={td}>{h.seen_on}</td><td className={td}>{h.store}</td>
                    <td className={td}>{money(h.price)} / {h.pack_qty}{h.on_sale ? " · sale" : ""}</td>
                    <td className={td}>{money(h.unit_price, 3)}</td><td className={`${td} text-xs text-slate-500`}>{h.source}</td>
                    <td className={td}><button onClick={() => deletePrice(h.id)} aria-label="Remove price" className="text-slate-500 hover:text-red-400"><Trash2 size={14} /></button></td>
                  </tr>
                ))}
                {history && !history.rows.length && <tr><td className={td} colSpan={6}>No prices yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sourcing;
